import {Component, computed, effect, inject, signal} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {ElciaDialog} from '../../../dialog/dialog';
import {Popover} from 'primeng/popover';
import {TranslatePipe} from '@ngx-translate/core';
import {Avatar} from 'primeng/avatar';
import {CurrentUserService} from '../../../../services/current-user.service';
import {ProfileCommandService} from '../../../../services/profile-command.service';
import {TokenService} from '../../../../auth/token.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {TextInput} from '../../../text-input/text-input';
import {LocalDropdown} from '../../../local-dropdown/local-dropdown';
import {BearerImagePipe} from '../../../../pipes/bearer-image.pipe';

@Component({
  selector: 'elcia-my-profile',
  imports: [
    ElciaDialog,
    Popover,
    TranslatePipe,
    Avatar,
    TextInput,
    LocalDropdown,
    BearerImagePipe,
    AsyncPipe
  ],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.scss',
})
export class MyProfile {

  private readonly currentUserService = inject(CurrentUserService);
  protected readonly profileCommand = inject(ProfileCommandService);
  private readonly tokenService = inject(TokenService);

  protected readonly isFormDisplayed = signal(false);
  readonly currentUser = computed(() => this.currentUserService.currentUser());
  protected readonly avatarUrl = computed(() => this.currentUserService.avatarUrl());

  // Avatar picked in the edit form (not yet saved) and its local preview URL.
  protected readonly selectedAvatar = signal<File | null>(null);
  protected readonly avatarPreview = signal<string | null>(null);

  fg = new FormGroup({
    lastName: new FormControl<string>({value: '', disabled: true}),
    firstName: new FormControl<string>({value: '', disabled: true}),
    locale: new FormControl<string>('', Validators.required)
  });


  onCurrentUserChange = effect(() => {
    if (this.currentUser()) {
      this.fg.controls.locale.setValue(this.currentUser()?.localeIsoCode ?? '');
      this.fg.controls.firstName.setValue(this.currentUser()?.firstName ?? '');
      this.fg.controls.lastName.setValue(this.currentUser()?.lastName ?? '');
    }
  });

  protected editProfile(): void {
    this.isFormDisplayed.set(true);
  }

  protected logout(): void {
    this.tokenService.logout();
  }

  protected closeDialog() {
    this.isFormDisplayed.set(false);
    this.selectedAvatar.set(null);
    this.setAvatarPreview(null);
  }

  protected onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedAvatar.set(file);
    this.setAvatarPreview(file ? URL.createObjectURL(file) : null);
  }

  // Revoke the previous blob URL before replacing it, otherwise the browser never frees it.
  private setAvatarPreview(url: string | null): void {
    const previous = this.avatarPreview();
    if (previous) {
      URL.revokeObjectURL(previous);
    }
    this.avatarPreview.set(url);
  }

  protected saveProfile() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    }

    const value = this.fg.getRawValue();
    this.profileCommand.updateProfileMutation.mutate(
      {
        firstName: value.firstName,
        lastName: value.lastName,
        localeIsoCode: value.locale!,
        avatar: this.selectedAvatar(),
      },
      {
        onSuccess: () => this.closeDialog(),
      }
    );
  }
}
